import SmsIcon from '@mui/icons-material/Sms';
import VoicemailIcon from '@mui/icons-material/Voicemail';
import { Typography } from '@mui/material';
import { Call, Device } from '@twilio/voice-sdk';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNotify } from 'react-admin';
import Notification, { NotificationStack } from '../../components/common/Notification';
import { useDeviceLock } from '../../hooks/useDeviceLock';
import { usePusher } from '../../hooks/usePusher';
import {
  AgentAvailabilityStatus,
  AgentDirectoryEntry,
  CallDirection,
  CallLog,
  CallPurpose,
  Conference,
  ParticipantType,
  SimplifiedMember,
  TransferType,
} from '../../interfaces/calls';
import {
  AgentStatusEventPayload,
  CallTransferEventName,
  CommunicationCenterEventName,
  ParticipantEventName,
  ParticipantEventPayload,
  SmsEventPayload,
  TransferEventPayload,
  VoicemailEventPayload,
} from '../../interfaces/pusher-events';
import { AuthService } from '../../services';
import voiceService from '../../services/appointments/calls';
import { reportMonitoredError } from '../../services/sentry';
import { sessionMonitor } from '../../services/session';
import { getLocalTimeString } from '../../util/dateFormat';
import { getCapitalizedWord } from '../../util/stringFormat';
import { sleep } from '../../utils/async.utils';
import {
  AgentBootstrapState,
  CallState,
  CallStatus,
  PhoneCallsContextType,
  TransferCallData,
} from './phone-calls.types';

const INVALID_AGENT_AVAILABILITY_STATUSES = [
  AgentAvailabilityStatus.ON_CALL,
  AgentAvailabilityStatus.AWAY,
  AgentAvailabilityStatus.OFFLINE,
  AgentAvailabilityStatus.DISABLED,
];

const PhoneCallsContext = createContext<PhoneCallsContextType | undefined>(undefined);

interface PhoneCallsProviderProps {
  children: ReactNode;
}

const initialCallState: CallState = {
  call: null,
  status: CallStatus.Idle,
  direction: null,
  muted: false,
  onHold: false,
};

export const PhoneCallsProvider: React.FC<PhoneCallsProviderProps> = ({ children }) => {
  const notify = useNotify();
  const { acquireLock, releaseLock, isLockedByOtherTab } = useDeviceLock();

  // Agent State
  const [agent, setAgent] = useState<Omit<AgentDirectoryEntry, 'status'> | null>(null);

  const agentRef = useRef<Omit<AgentDirectoryEntry, 'status'> | null>(null);

  const agentAvailabilityRef = useRef<{
    currentStatus: AgentAvailabilityStatus;
    previousStatus: AgentAvailabilityStatus;
  }>({
    previousStatus: AgentAvailabilityStatus.OFFLINE,
    currentStatus: AgentAvailabilityStatus.OFFLINE,
  });

  const [, forceUpdateAgentAvailability] = useState({});

  // Bootstrap status, so a failure to load the agent record is visible and
  // recoverable rather than an unexplained missing phone widget.
  const [agentBootstrapState, setAgentBootstrapState] = useState<AgentBootstrapState>('loading');
  const [agentBootstrapAttempt, setAgentBootstrapAttempt] = useState(0);
  const retryAgentBootstrap = useCallback(() => {
    setAgentBootstrapAttempt((attempt) => attempt + 1);
  }, []);

  // Device State (for outgoing calls and general device management)
  const [device, setDevice] = useState<Device | null>(null);
  const [isDeviceOn, setIsDeviceOn] = useState<boolean>(false);
  const [activeCallState, setActiveCallState] = useState<CallState>(initialCallState);

  // Conference State
  const [conference, setConference] = useState<Conference | null>(null);

  // Active Call State
  const [currentCallSid, setCurrentCallSid] = useState<string | null>(null);

  // Call State Computed Properties
  const [callDuration, setCallDuration] = useState<number>(0);
  const [callLog, setCallLog] = useState<CallLog | null>(null);

  // Call Purpose State
  const [callPurpose, setCallPurpose] = useState<CallPurpose>(CallPurpose.GENERAL_INQUIRY);

  // Incoming Call State
  const [isAgentReceivingCall, setIsAgentReceivingCall] = useState<boolean>(false);
  const [incomingCallType, setIncomingCallType] = useState<'member' | 'transfer' | 'agent'>(
    'member',
  );

  // Modal States
  const [isOutgoingCallModalOpen, setIsOutgoingCallModalOpen] = useState(false);
  const [isIncomingCallModalOpen, setIsIncomingCallModalOpen] = useState(false);
  const [isDirectoryOpen, setIsDirectoryOpen] = useState(false);

  // Navigation State
  const [returnToCall, setReturnToCall] = useState<boolean>(false);

  // Transfer State
  const [transferData, setTransferData] = useState<TransferCallData | null>(null);
  const [agentDirectory, setAgentDirectory] = useState<AgentDirectoryEntry[]>([]);
  const [agentDirectoryLastUpdatedAt, setAgentDirectoryLastUpdatedAt] = useState<Date | null>(null);

  // Rating State
  const [isAwaitingRating, setIsAwaitingRating] = useState<boolean>(false);
  const [audioRating, setAudioRating] = useState<number | null>(0);
  const [isSavingRating, setIsSavingRating] = useState<boolean>(false);

  // Notes State
  const [callNotes, setCallNotes] = useState<string>('');

  const [voicemailNotifications, setVoicemailNotifications] = useState<
    Array<{
      id: string;
      fromName: string;
      fromNumber: string;
      duration: string;
    }>
  >([]);

  const [smsNotifications, setSmsNotifications] = useState<
    Array<{
      id: string;
      fromNumber: string;
    }>
  >([]);

  // Selected Member State
  const [selectedMember, setSelectedMember] = useState<SimplifiedMember | null>(null);

  // Phone Number State
  const [memberPhoneNumber, setMemberPhoneNumber] = useState<string>('');

  // Computed Properties
  const isMuted = useMemo(() => {
    return activeCallState.muted;
  }, [activeCallState.muted]);

  const isOnHold = useMemo(() => {
    if (activeCallState.direction === CallDirection.OUTBOUND) {
      return activeCallState.onHold;
    } else if (activeCallState.direction === CallDirection.INBOUND) {
      return activeCallState.onHold;
    }
    return false;
  }, [activeCallState.direction, activeCallState.onHold]);

  // Refs
  const tokenRefreshInterval = useRef<NodeJS.Timeout | null>(null);
  const callDurationTimer = useRef<NodeJS.Timeout | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const isDeviceDestroyed = useRef<boolean>(false);

  // Reconnection management constants
  const RECONNECT_BASE_DELAY = 1000; // 1 second
  const RECONNECT_MAX_DELAY = 30000; // 30 seconds
  const MAX_RECONNECT_ATTEMPTS = 10;

  // Reconnection management refs
  const isReconnecting = useRef<boolean>(false);
  const reconnectAttempts = useRef<number>(0);
  const reconnectTimeout = useRef<NodeJS.Timeout | null>(null);

  // Circuit breaker for the periodic agent-directory poll, so a persistent
  // failure stops re-notifying instead of toasting every 45 seconds forever.
  const MAX_DIRECTORY_FAILURES = 3;
  const directoryFailures = useRef<number>(0);
  const directoryBreakerOpen = useRef<boolean>(false);

  // Refs for Pusher event handlers (to avoid re-subscription on value changes)
  const memberPhoneNumberRef = useRef<string>('');
  const selectedMemberRef = useRef<SimplifiedMember | null>(null);

  // Pusher WebSocket connection for real-time events.
  // Kept open while the agent is logged in (regardless of Twilio device state)
  // so SMS and voicemail notifications still arrive when the agent has turned
  // their phone line off — calls are gated separately by isDeviceOn.
  const pusher = usePusher({
    appKey: process.env.REACT_APP_PUSHER_APP_KEY || '',
    cluster: process.env.REACT_APP_PUSHER_CLUSTER || 'mt1',
    debug: process.env.NODE_ENV === 'development',
    enabled: agent !== null,
  });

  // Keep refs in sync with state for Pusher event handlers
  useEffect(() => {
    memberPhoneNumberRef.current = memberPhoneNumber;
  }, [memberPhoneNumber]);

  useEffect(() => {
    selectedMemberRef.current = selectedMember;
  }, [selectedMember]);

  // Helper Functions
  const calculateBackoff = useCallback(
    (attempt: number): number => {
      const delay = Math.min(RECONNECT_BASE_DELAY * Math.pow(2, attempt), RECONNECT_MAX_DELAY);
      return delay;
    },
    [RECONNECT_BASE_DELAY, RECONNECT_MAX_DELAY],
  );

  const canPerformDeviceOperation = useCallback((device: Device): boolean => {
    // Check if device is in a valid state for token update or registration
    const validStates = [
      Device.State.Registered,
      Device.State.Registering,
      Device.State.Unregistered,
    ];

    if (isDeviceDestroyed.current) {
      console.log(`[${getLocalTimeString()}] Device operation blocked: device is destroyed`);
      return false;
    }

    if (!validStates.includes(device.state)) {
      console.log(
        `[${getLocalTimeString()}] Device operation blocked: invalid state ${device.state}`,
      );
      return false;
    }

    return true;
  }, []);

  const safelyUpdateDeviceToken = useCallback(
    async (device: Device): Promise<boolean> => {
      if (!canPerformDeviceOperation(device)) {
        return false;
      }

      try {
        const tokenResponse = await voiceService.generateAccessToken();

        if (!tokenResponse.success || !tokenResponse.data?.token) {
          console.error(`[${getLocalTimeString()}] Failed to generate access token`);
          return false;
        }

        console.log(`[${getLocalTimeString()}] Updating device token...`);
        device.updateToken(tokenResponse.data.token);
        console.log(`[${getLocalTimeString()}] Device token updated successfully`);
        return true;
      } catch (error) {
        console.error(`[${getLocalTimeString()}] Error updating device token:`, error);
        return false;
      }
    },
    [canPerformDeviceOperation],
  );

  const safelyRegisterDevice = useCallback(
    async (device: Device): Promise<boolean> => {
      if (!canPerformDeviceOperation(device)) {
        return false;
      }

      if (device.state !== Device.State.Unregistered) {
        console.log(
          `[${getLocalTimeString()}] Device registration skipped: already in state ${device.state}`,
        );
        return true;
      }

      try {
        console.log(`[${getLocalTimeString()}] Registering device...`);
        device.register();
        console.log(`[${getLocalTimeString()}] Device registered successfully`);
        return true;
      } catch (error) {
        console.error(`[${getLocalTimeString()}] Error registering device:`, error);
        return false;
      }
    },
    [canPerformDeviceOperation],
  );

  /**
   * Take the device fully offline and make the UI say so.
   *
   * The bug this exists to fix: when the device went offline or gave up
   * reconnecting, `isDeviceOn` stayed `true` and the agent's status stayed
   * `AVAILABLE`. The app bar kept advertising the agent as reachable while
   * Twilio had unregistered them, so inbound calls were routed to a dead client
   * and silently lost. This is the single shared teardown for both the
   * give-up-reconnecting path and session expiry.
   *
   * Deliberately does not call `updateAgentAvailability(OFFLINE)`. On session
   * expiry that request would fail (the token is dead) and re-trigger the
   * session monitor. The server-side status therefore stays `AVAILABLE` until a
   * backend heartbeat/TTL exists — the frontend can only stop lying locally.
   */
  const markDeviceOffline = useCallback(
    (reason: string) => {
      console.warn(`[${getLocalTimeString()}] Taking device offline: ${reason}`);

      isDeviceDestroyed.current = true;
      isReconnecting.current = false;
      reconnectAttempts.current = 0;

      [tokenRefreshInterval, reconnectTimeout, callDurationTimer].forEach((timer) => {
        if (timer.current) {
          clearTimeout(timer.current as NodeJS.Timeout);
          clearInterval(timer.current as NodeJS.Timeout);
          timer.current = null;
        }
      });

      try {
        device?.removeAllListeners();
        device?.destroy();
      } catch (error) {
        console.error(`[${getLocalTimeString()}] Error destroying device:`, error);
      }

      setDevice(null);
      setIsDeviceOn(false);
      releaseLock();

      agentAvailabilityRef.current = {
        previousStatus: AgentAvailabilityStatus.OFFLINE,
        currentStatus: AgentAvailabilityStatus.OFFLINE,
      };
      // agentAvailability lives in a ref, so a render has to be forced for the
      // app bar to reflect the change.
      forceUpdateAgentAvailability({});
    },
    [device, releaseLock],
  );

  const attemptReconnection = useCallback(
    async (device: Device): Promise<void> => {
      // Nothing to reconnect to with a dead session: every /calls/token request
      // would 401. Without this the loop hammered the API for 2-3 minutes.
      if (sessionMonitor.isSessionDead()) {
        return;
      }

      // Prevent concurrent reconnection attempts
      if (isReconnecting.current) {
        console.log(`[${getLocalTimeString()}] Reconnection already in progress, skipping`);
        return;
      }

      if (reconnectAttempts.current >= MAX_RECONNECT_ATTEMPTS) {
        console.error(
          `[${getLocalTimeString()}] Max reconnection attempts (${MAX_RECONNECT_ATTEMPTS}) reached, giving up`,
        );
        notify('Unable to reconnect to Twilio. Please refresh the page.', {
          type: 'error',
        });
        // Previously this only showed a toast, leaving the UI claiming the agent
        // was still available.
        markDeviceOffline('reconnection-exhausted');
        return;
      }

      isReconnecting.current = true;
      const currentAttempt = reconnectAttempts.current;
      const backoffDelay = calculateBackoff(currentAttempt);

      console.log(
        `[${getLocalTimeString()}] Reconnection attempt ${
          currentAttempt + 1
        }/${MAX_RECONNECT_ATTEMPTS} - waiting ${backoffDelay}ms`,
      );

      // Clear any existing timeout
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }

      reconnectTimeout.current = setTimeout(async () => {
        try {
          console.log(
            `[${getLocalTimeString()}] Executing reconnection attempt ${currentAttempt + 1}`,
          );

          // Step 1: Update token
          const tokenUpdated = await safelyUpdateDeviceToken(device);
          if (!tokenUpdated) {
            throw new Error('Failed to update token');
          }

          // Step 2: Register device if unregistered
          const deviceRegistered = await safelyRegisterDevice(device);
          if (!deviceRegistered) {
            throw new Error('Failed to register device');
          }

          console.log(`[${getLocalTimeString()}] Reconnection successful`);
          reconnectAttempts.current = 0;
          isReconnecting.current = false;
          notify('Reconnected to Twilio successfully', { type: 'success' });
        } catch (error) {
          console.error(
            `[${getLocalTimeString()}] Reconnection attempt ${currentAttempt + 1} failed:`,
            error,
          );

          // Increment attempt counter and try again
          reconnectAttempts.current += 1;
          isReconnecting.current = false;

          // Recursively attempt reconnection
          await attemptReconnection(device);
        }
      }, backoffDelay);
    },
    [
      MAX_RECONNECT_ATTEMPTS,
      calculateBackoff,
      safelyUpdateDeviceToken,
      safelyRegisterDevice,
      notify,
      markDeviceOffline,
    ],
  );

  const startCallTimer = useCallback(() => {
    if (callDurationTimer.current) {
      clearInterval(callDurationTimer.current);
    }
    setCallDuration(0);
    callDurationTimer.current = setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);
  }, []);

  const stopCallTimer = useCallback(() => {
    if (callDurationTimer.current) {
      clearInterval(callDurationTimer.current);
      callDurationTimer.current = null;
    }
  }, []);

  const fetchCallLog = useCallback(async (callSid: string) => {
    const maxAttempts = 5;
    const delayMs = 1000;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const response = await voiceService.searchCalls({ callSid });

        if (response.success && response.data && response.data.length > 0) {
          setCallLog(response.data[0]);
          return;
        }

        console.log(`Call log not found for ${callSid}, attempt ${attempt}/${maxAttempts}`);
      } catch (error) {
        console.error(`Error fetching call log (attempt ${attempt}/${maxAttempts}):`, error);
      }

      if (attempt < maxAttempts) {
        await sleep(delayMs);
      }
    }

    console.warn(`Failed to fetch call log for ${callSid} after ${maxAttempts} attempts`);
  }, []);

  const syncHoldStateFromConference = useCallback(
    (conference: Conference) => {
      if (!agent || !conference.participants) {
        return;
      }

      // Find external participants (users/members)
      const externalParticipants = conference.participants.filter(
        (p: any) => p.participantType === 'EXTERNAL',
      );

      // Check if any external participant is on hold
      const isAnyExternalOnHold = externalParticipants.some((p: any) => p.hold);

      // Update local state only if different to avoid unnecessary re-renders
      setActiveCallState((prev) => {
        if (prev.onHold !== isAnyExternalOnHold) {
          return { ...prev, onHold: isAnyExternalOnHold };
        }
        return prev;
      });
    },
    [agent],
  );

  const syncMutedStateFromConference = useCallback(
    (conference: Conference) => {
      if (!agent || !conference.participants) {
        return;
      }

      // Find current agent's participant record
      const currentAgentParticipant = conference.participants.find(
        (p: any) => p.coreProviderId === agent.coreProviderId,
      );

      if (currentAgentParticipant) {
        // Sync muted state only if different
        setActiveCallState((prev) => {
          if (prev.muted !== currentAgentParticipant.muted) {
            return { ...prev, muted: currentAgentParticipant.muted };
          }
          return prev;
        });
      }
    },
    [agent],
  );

  // Conference Management Functions
  const fetchConferenceByCallSid = useCallback(
    async (callSid: string) => {
      try {
        const response = await voiceService.getConferenceByCallSid(callSid);
        if (response.success && response.data) {
          setConference(response.data);

          // Sync hold state from conference participants
          syncHoldStateFromConference(response.data);

          // Sync muted state from conference participants
          syncMutedStateFromConference(response.data);

          // Try to find external participant first (for regular calls)
          const externalParticipant = response.data.participants?.find(
            (participant) => participant.participantType === ParticipantType.External,
          );

          // If no external participant, find the other agent (for agent-to-agent calls)
          const otherAgentParticipant = !externalParticipant
            ? response.data.participants?.find(
                (participant) =>
                  participant.participantType === ParticipantType.Agent &&
                  participant.coreProviderId !== agent?.coreProviderId,
              )
            : undefined;

          const participantPhone =
            externalParticipant?.phoneNumber || otherAgentParticipant?.phoneNumber;

          if (participantPhone) {
            setMemberPhoneNumber(participantPhone);
          }
        }
      } catch (error) {
        console.error('Failed to fetch conference:', error);
      }
    },
    [agent, syncHoldStateFromConference, syncMutedStateFromConference],
  );

  // Computed Properties for Transfer
  const canInitiateTransfer = useMemo(() => {
    return !!(activeCallState.call !== null && conference !== null);
  }, [activeCallState.call, conference]);

  // Effect to manage call timer
  useEffect(() => {
    if (activeCallState.call && currentCallSid) {
      startCallTimer();
      fetchCallLog(currentCallSid);
    } else {
      stopCallTimer();
      setCallDuration(0);
    }

    return () => {
      stopCallTimer();
    };
  }, [
    activeCallState.call,
    currentCallSid,
    startCallTimer,
    stopCallTimer,
    fetchCallLog,
    isAwaitingRating,
  ]);

  // Effect to poll conference state during active call
  useEffect(() => {
    // Only poll when there's an active call with callSid and not idle
    if (!currentCallSid || !activeCallState.call || activeCallState.status === CallStatus.Idle) {
      return;
    }

    // Initial fetch
    // TODO: Here i need to implement a strategy to get fresh data and reflect real agents status
    fetchConferenceByCallSid(currentCallSid);
  }, [currentCallSid, activeCallState.call, activeCallState.status, fetchConferenceByCallSid]);

  // Agent Management Functions
  const updateAgentAvailability = useCallback(
    async ({ status: newStatus }: { status: AgentAvailabilityStatus }) => {
      try {
        const prevAgentAvailabilityStatus = agentAvailabilityRef.current;

        const oldStatus = INVALID_AGENT_AVAILABILITY_STATUSES.includes(
          prevAgentAvailabilityStatus.currentStatus,
        )
          ? AgentAvailabilityStatus.AVAILABLE
          : prevAgentAvailabilityStatus.currentStatus;

        agentAvailabilityRef.current = {
          previousStatus: oldStatus,
          currentStatus: newStatus,
        };

        const response = await voiceService.updateAgentAvailability({
          status: newStatus,
          coreProviderId: agentRef.current?.coreProviderId,
        });

        if (response.success) {
          forceUpdateAgentAvailability({});

          notify(`Status updated to ${getCapitalizedWord(newStatus)}`, { type: 'success' });
        } else {
          agentAvailabilityRef.current = prevAgentAvailabilityStatus;
          if (response.statusCode === 404) {
            notify('Agent profile not found. Contact an administrator.', { type: 'error' });
          } else {
            notify('Failed to update status', { type: 'error' });
          }
        }
      } catch (error) {
        console.error('Error updating agent availability:', error);
        notify('Failed to update status - please check your connection', { type: 'error' });
      }
    },
    [notify],
  );

  const getAgentAvailability = useCallback(async () => {
    try {
      const response = await voiceService.getAgentStatus();
      return response.data?.status || null;
    } catch (error) {
      console.error('Error fetching agent availability:', error);
      notify('Failed to fetch status - please check your connection', { type: 'error' });
    }
  }, [notify]);

  // Call State Update Functions
  const updateActiveCallState = useCallback((updates: Partial<CallState>) => {
    setActiveCallState((prev) => ({ ...prev, ...updates }));
  }, []);

  const muteCall = useCallback(
    async (muted: boolean) => {
      const targetCall = activeCallState.call;

      if (!targetCall) {
        console.error(`No active call to mute/unmute on  device`);
        return;
      }

      if (!callLog || !agent) {
        console.error('No active call log or agent to mute/unmute');
        return;
      }

      // Get agent's phone number from tracking numbers
      const agentPhone =
        targetCall.direction === Call.CallDirection.Incoming
          ? agent.trackingNumbers?.incoming?.[0]?.number
          : agent.trackingNumbers?.outgoing?.[0]?.number;

      if (!agentPhone) {
        console.error('No phone number found for agent');
        notify('Failed to mute/unmute - agent phone not found', { type: 'error' });
        return;
      }

      try {
        // Mute locally for instant feedback
        targetCall.mute(muted);
        console.log(`Call ${muted ? 'muted' : 'unmuted'} locally on device`);

        // Update local state immediately
        updateActiveCallState({ muted });

        // Update backend
        const response = await voiceService.muteUnmuteConference({
          muted,
          callLogId: callLog.id,
          participantPhone: agentPhone,
        });

        if (response.success) {
          console.log(`Participant ${muted ? 'muted' : 'unmuted'} successfully in backend`);
        } else {
          notify(`Failed to update mute status on server: ${response.message}`, {
            type: 'warning',
          });
        }
      } catch (error) {
        console.error(`Error muting/unmuting call:`, error);
        notify(`Failed to mute/unmute call`, { type: 'error' });
      }
    },
    [activeCallState.call, callLog, agent, notify, updateActiveCallState],
  );

  const holdUnholdConference = useCallback(
    async (hold: boolean, participantPhone: string) => {
      if (!callLog) {
        console.error('No active call log to hold/unhold participant');
        notify('No active call to hold/unhold', { type: 'error' });
        return;
      }

      try {
        const response = await voiceService.holdUnholdConference({
          hold,
          callLogId: callLog.id,
          participantPhone,
        });

        if (response.success) {
          updateActiveCallState({ onHold: hold });
          notify(`Participant ${hold ? 'put on hold' : 'removed from hold'} successfully`, {
            type: 'success',
          });
          console.log(`Participant ${hold ? 'held' : 'unheld'} successfully`);
        } else {
          notify(`Failed to ${hold ? 'hold' : 'unhold'} participant: ${response.message}`, {
            type: 'error',
          });
        }
      } catch (error) {
        console.error(`Error ${hold ? 'holding' : 'unholding'} participant:`, error);
        notify(`Failed to ${hold ? 'hold' : 'unhold'} participant`, { type: 'error' });
      }
    },
    [callLog, notify, updateActiveCallState],
  );

  const sendDigitsOnCall = useCallback(
    (digits: string) => {
      const targetCall = activeCallState.call;

      if (!targetCall) {
        console.error(`No active call to send digits`);
        return;
      }

      try {
        targetCall.sendDigits(digits);
        console.log(`Send ${digits} through the call`);
      } catch (error) {
        console.error(`Error sending digits through the call:`, error);
        notify(`Failed to send digits through the call`, { type: 'error' });
      }
    },
    [activeCallState.call, notify],
  );

  // Cancel Transfer
  const cancelTransfer = useCallback(async () => {
    if (!transferData) {
      return;
    }

    try {
      const response = await voiceService.cancelTransfer(transferData.transferCallLogId);

      if (!response.success) {
        throw new Error(response.message || 'Failed to cancel transfer');
      }

      setTransferData(null);

      notify('Transfer cancelled', { type: 'info' });
    } catch (error) {
      console.error('Error cancelling transfer:', error);
      notify('Failed to cancel transfer', { type: 'error' });
    }
  }, [transferData, notify]);

  /**
   * Refresh the transfer directory. Returns whether it succeeded so the 45s
   * poller can trip its circuit breaker.
   *
   * This used to notify on every failure. Combined with the 45s interval, an
   * expired session produced a red toast every 45 seconds indefinitely — which
   * trains people to ignore toasts, including the ones that matter.
   */
  const refreshAgentDirectory = useCallback(async (): Promise<boolean> => {
    const failed = (error?: unknown) => {
      if (error) {
        console.error('Error refreshing agent directory:', error);
      }
      directoryFailures.current += 1;
      // Warn once on the first failure, then once more when the breaker trips.
      if (directoryFailures.current === 1) {
        notify('Failed to load agent directory', { type: 'error' });
      } else if (directoryFailures.current === MAX_DIRECTORY_FAILURES) {
        directoryBreakerOpen.current = true;
        notify('Agent directory is unavailable. Refresh the page to retry.', { type: 'error' });
      }
      return false;
    };

    try {
      const response = await voiceService.getAgentDirectory();
      if (response.success && response.data) {
        const agentList = Object.values(response.data);
        setAgentDirectory(agentList);
        setAgentDirectoryLastUpdatedAt(new Date());
        directoryFailures.current = 0;
        directoryBreakerOpen.current = false;
        return true;
      }
      return failed();
    } catch (error) {
      return failed(error);
    }
  }, [notify]);

  // Initiate Warm Transfer
  const initiateWarmTransfer = useCallback(
    async (targetAgent: AgentDirectoryEntry) => {
      if (!activeCallState.call || !callLog) {
        notify('Cannot initiate transfer: No active call', { type: 'error' });
        return;
      }

      if (!callLog.conferenceName) {
        notify('Cannot initiate transfer: No conference session', { type: 'error' });
        return;
      }

      try {
        // 1. Put caller on hold first
        const callerPhone = memberPhoneNumber || selectedMember?.phone;

        if (callerPhone) {
          await holdUnholdConference(true, callerPhone);
        }

        notify(`Calling ${targetAgent.name}...`, { type: 'info' });

        // 2. Initiate transfer via backend API
        const response = await voiceService.initiateTransfer(callLog.conferenceName, {
          type: TransferType.WARM,
          targetCoreProviderId: targetAgent.coreProviderId,
        });

        if (!response.success || !response.data) {
          throw new Error(response.message || 'Failed to initiate transfer');
        }

        const transferData: TransferCallData = {
          transferCallLogId: response.data.transferCallLogId,
          transferType: 'WARM',
          status: 'initiated',
          targetAgent: {
            id: targetAgent.id,
            name: response.data.targetAgentName,
            coreProviderId: targetAgent.coreProviderId,
          },
          participantCallSid: response.data.participantCallSid,
        };

        setTransferData(transferData);
        notify(`Transfer initiated. Calling ${response.data.targetAgentName}...`, {
          type: 'success',
        });

        // TODO: Poll for transfer status updates or implement WebSocket listener
      } catch (error) {
        console.error('Error initiating warm transfer:', error);
        const errorMessage = (error as Error).message || 'Failed to initiate transfer';
        notify(errorMessage, { type: 'error' });

        // NOTE: Caller remains on hold even if transfer fails
        // Agent must manually unhold if they want to continue with the original call
      }
    },
    [
      activeCallState.call,
      callLog,
      notify,
      memberPhoneNumber,
      selectedMember?.phone,
      holdUnholdConference,
    ],
  );

  // Initiate Blind Transfer
  const initiateBlindTransfer = useCallback(
    async (targetAgent: AgentDirectoryEntry) => {
      if (!activeCallState.call || !callLog) {
        notify('Cannot initiate transfer: No active call', { type: 'error' });
        return;
      }

      if (!callLog.conferenceName) {
        notify('Cannot initiate transfer: No conference session', { type: 'error' });
        return;
      }

      try {
        // 1. Put caller on hold first
        const callerPhone = memberPhoneNumber || selectedMember?.phone;
        if (callerPhone) {
          await holdUnholdConference(true, callerPhone);
        }

        notify(`Transferring to ${targetAgent.name}...`, { type: 'info' });

        // 2. Initiate transfer via backend API
        const response = await voiceService.initiateTransfer(callLog.conferenceName, {
          type: TransferType.BLIND,
          targetCoreProviderId: targetAgent.coreProviderId,
        });

        if (!response.success || !response.data) {
          throw new Error(response.message || 'Failed to initiate transfer');
        }

        notify(
          `Blind transfer initiated to ${response.data.targetAgentName}. Transfer will complete automatically when they answer.`,
          { type: 'success' },
        );

        // For blind transfers, we don't need to track the state since backend auto-completes
        // We can immediately clear our local state
        setTransferData(null);
      } catch (error) {
        console.error('Error initiating blind transfer:', error);
        const errorMessage = (error as Error).message || 'Failed to initiate blind transfer';
        notify(errorMessage, { type: 'error' });
      }
    },
    [
      activeCallState.call,
      callLog,
      notify,
      memberPhoneNumber,
      selectedMember?.phone,
      holdUnholdConference,
    ],
  );

  const clearCallData = useCallback(() => {
    setCallLog(null);
    setCurrentCallSid(null);
    setAgent((prev) => (prev ? { ...prev, currentCallId: undefined } : null));
    setIsAwaitingRating(false);
    setAudioRating(0);
    setIsSavingRating(false);
    setCallNotes('');
    setSelectedMember(null);
    setMemberPhoneNumber('');
    setConference(null);
    setIsDirectoryOpen(false);
    setTransferData(null);
    setIsIncomingCallModalOpen(false);
    setIsOutgoingCallModalOpen(false);
    setCallPurpose(CallPurpose.GENERAL_INQUIRY);

    updateActiveCallState({
      call: null,
      status: CallStatus.Idle,
      direction: null,
      onHold: false,
      muted: false,
    });
  }, [updateActiveCallState]);

  // Outgoing Call Event Listeners (for device calls)
  const setupOutgoingCallEventListeners = useCallback(
    (call: Call) => {
      console.log('Setting up outgoing call event listeners');

      // Accept event (when outgoing call is answered by the recipient)
      call.on('accept', (acceptedCall: Call) => {
        console.log(
          `[${getLocalTimeString()}] Outgoing call accepted:`,
          acceptedCall.parameters.CallSid,
        );
        setCurrentCallSid(acceptedCall.parameters.CallSid);

        updateAgentAvailability({ status: AgentAvailabilityStatus.ON_CALL });
        updateActiveCallState({
          status: CallStatus.OnCall,
          startTime: new Date(),
        });
      });

      // Disconnect event (when outgoing call ends)
      call.on('disconnect', () => {
        console.log(`[${getLocalTimeString()}] Outgoing call disconnected`);
        notify('The outgoing call has been disconnected');

        updateActiveCallState({
          call: null,
          status: CallStatus.Ended,
          endTime: new Date(),
          onHold: false,
          direction: null,
        });

        setIsAwaitingRating(true);
        updateAgentAvailability({ status: agentAvailabilityRef.current.previousStatus });
      });

      // Cancel event (when outgoing call is canceled)
      call.on('cancel', () => {
        console.log(`[${getLocalTimeString()}] Outgoing call canceled`);

        updateActiveCallState({
          call: null,
          status: CallStatus.Idle,
          onHold: false,
          direction: null,
        });

        updateAgentAvailability({ status: agentAvailabilityRef.current.previousStatus });
      });

      // Reject event (when outgoing call is rejected by recipient)
      call.on('reject', () => {
        console.log(`[${getLocalTimeString()}] Outgoing call rejected`);
        notify('The outgoing call was rejected');

        setCurrentCallSid(null);

        updateActiveCallState({
          call: null,
          status: CallStatus.Rejected,
          onHold: false,
          direction: null,
        });

        updateAgentAvailability({ status: agentAvailabilityRef.current.previousStatus });

        clearCallData();
      });

      // Error event (when outgoing call encounters an error)
      call.on('error', (error) => {
        console.error(`[${getLocalTimeString()}] Outgoing call error:`, error);
        notify('Outgoing call error: ' + error.message, { type: 'error' });

        setCurrentCallSid(null);

        updateActiveCallState({
          call: null,
          status: CallStatus.Failed,
          endTime: new Date(),
          onHold: false,
          direction: null,
        });

        updateAgentAvailability({ status: agentAvailabilityRef.current.previousStatus });
      });

      // Mute event (when outgoing call mute status changes)
      call.on('mute', (muted: boolean) => {
        console.log(`[${getLocalTimeString()}] Outgoing call mute status changed:`, muted);
        updateActiveCallState({ muted });
      });

      // Reconnected event (when outgoing call reconnects)
      call.on('reconnected', () => {
        console.log(`[${getLocalTimeString()}] Outgoing call reconnected`);
        updateActiveCallState({ status: CallStatus.Reconnected });
        notify('Outgoing call reconnected', { type: 'success' });
      });

      // Reconnecting event (when outgoing call is attempting to reconnect)
      call.on('reconnecting', (twilioError) => {
        console.log(`[${getLocalTimeString()}] Outgoing call reconnecting:`, twilioError);
        updateActiveCallState({ status: CallStatus.Reconnecting });
        notify('Outgoing call reconnecting...', { type: 'warning' });
      });
    },
    [clearCallData, notify, updateActiveCallState, updateAgentAvailability],
  );

  // Incoming Call Event Listeners (for incoming device calls)
  const setupIncomingCallEventListeners = useCallback(
    (call: Call) => {
      console.log('Setting up incoming call event listeners');

      // Accept event (when incoming call is answered by agent)
      call.on('accept', (acceptedCall: Call) => {
        console.log(
          `[${getLocalTimeString()}] Incoming call accepted:`,
          acceptedCall.parameters.CallSid,
        );
        setCurrentCallSid(acceptedCall.parameters.CallSid);

        // Fetch conference details when call is accepted
        if (acceptedCall.parameters.CallSid) {
          fetchConferenceByCallSid(acceptedCall.parameters.CallSid);
        }

        updateAgentAvailability({ status: AgentAvailabilityStatus.ON_CALL });
        updateActiveCallState({
          status: CallStatus.OnCall,
          startTime: new Date(),
        });
      });

      // Disconnect event (when incoming call ends)
      call.on('disconnect', () => {
        console.log(`[${getLocalTimeString()}] Incoming call disconnected`);
        notify('The incoming call has been disconnected');

        setIsAgentReceivingCall(false);

        updateActiveCallState({
          call: null,
          status: CallStatus.Ended,
          endTime: new Date(),
          onHold: false,
          direction: null,
        });

        setIsAwaitingRating(true);

        updateAgentAvailability({ status: agentAvailabilityRef.current.previousStatus });
      });

      // Cancel event (when incoming call is canceled by caller)
      call.on('cancel', () => {
        console.log(`[${getLocalTimeString()}] Incoming call canceled by caller`);

        setIsAgentReceivingCall(false);

        updateActiveCallState({
          call: null,
          status: CallStatus.Idle,
          onHold: false,
          direction: null,
        });

        updateAgentAvailability({ status: agentAvailabilityRef.current.previousStatus });
      });

      // Reject event (when incoming call is rejected by agent)
      call.on('reject', () => {
        console.log(`[${getLocalTimeString()}] Incoming call rejected by agent`);
        notify('The incoming call was rejected');

        setIsAgentReceivingCall(false);
        setCurrentCallSid(null);

        updateActiveCallState({
          call: null,
          status: CallStatus.Rejected,
          onHold: false,
          direction: null,
        });

        updateAgentAvailability({ status: AgentAvailabilityStatus.DO_NOT_DISTURB });
        clearCallData();
      });

      // Error event (when incoming call encounters an error)
      call.on('error', (error) => {
        console.error(`[${getLocalTimeString()}] Incoming call error:`, error);
        notify('Incoming call error: ' + error.message, { type: 'error' });

        setIsAgentReceivingCall(false);
        setCurrentCallSid(null);

        updateActiveCallState({
          call: null,
          status: CallStatus.Failed,
          endTime: new Date(),
          onHold: false,
          direction: null,
        });

        updateAgentAvailability({ status: agentAvailabilityRef.current.previousStatus });
      });

      // Mute event (when incoming call mute status changes)
      call.on('mute', (muted: boolean) => {
        console.log(`[${getLocalTimeString()}] Incoming call mute status changed:`, muted);
        updateActiveCallState({ muted });
      });

      // Reconnected event (when incoming call reconnects)
      call.on('reconnected', () => {
        console.log(`[${getLocalTimeString()}] Incoming call reconnected`);
        updateActiveCallState({ status: CallStatus.Reconnected });
        notify('Incoming call reconnected', { type: 'success' });
      });

      // Reconnecting event (when incoming call is attempting to reconnect)
      call.on('reconnecting', (twilioError) => {
        console.log(`[${getLocalTimeString()}] Incoming call reconnecting:`, twilioError);
        updateActiveCallState({ status: CallStatus.Reconnecting });
        notify('Incoming call reconnecting...', { type: 'warning' });
      });
    },
    [
      clearCallData,
      fetchConferenceByCallSid,
      notify,
      updateActiveCallState,
      updateAgentAvailability,
    ],
  );

  // Device Event Listeners
  const setupDeviceEventListeners = useCallback(
    async (device: Device) => {
      console.log('Setting up device event listeners');

      const status = await getAgentAvailability();

      if (status) {
        agentAvailabilityRef.current = { previousStatus: status, currentStatus: status };
        forceUpdateAgentAvailability({});
      } else {
        notify('Failed to update status', { type: 'error' });
      }

      device.on('ready', () => {
        console.log(`[${getLocalTimeString()}] Device is ready`);
        updateActiveCallState({ status: CallStatus.Idle });
      });

      device.on('registered', () => {
        console.log(`[${getLocalTimeString()}] Device is ready to receive incoming calls`);
        updateActiveCallState({ status: CallStatus.Idle });

        // Reset reconnection state on successful registration
        reconnectAttempts.current = 0;
        isReconnecting.current = false;
      });

      device.on('error', (error) => {
        console.error(`[${getLocalTimeString()}] Device error:`, error);
        notify('Device error: ' + error.message, { type: 'error' });
        updateActiveCallState({ status: CallStatus.Failed });
      });

      device.on('incoming', async (incomingCall: Call) => {
        console.log(
          `[${getLocalTimeString()}] Incoming call received on device, creating incoming device...`,
        );

        // With a dead session we cannot tell the server we are unavailable, so it
        // may still route calls here. Reject immediately so the call falls back to
        // the queue rather than ringing an agent who is about to be signed out.
        // (Reached only while an earlier call is still being protected — otherwise
        // the device has already been torn down.)
        if (sessionMonitor.isSessionDead()) {
          console.warn(`[${getLocalTimeString()}] Rejecting incoming call: session has expired`);
          incomingCall.reject();
          return;
        }

        // Setup event listeners for this incoming call
        setupIncomingCallEventListeners(incomingCall);

        // Determine incoming call type by fetching the call log
        let callType: 'member' | 'transfer' | 'agent' = 'member';
        try {
          const callSid = incomingCall.parameters.CallSid;
          const fromNumber = incomingCall.parameters.From;

          if (callSid) {
            const response = await voiceService.searchCalls({ callSid });

            if (response.success && response.data && response.data.length > 0) {
              const callLogData = response.data[0];

              // Check if it's a transfer
              if (callLogData.isTransfer) {
                callType = 'transfer';
              }
              // Check if it's from an agent (client identifier format)
              else if (fromNumber && fromNumber.startsWith('client:agent-')) {
                callType = 'agent';
              }
              // Otherwise it's a regular member call
              else {
                callType = 'member';
              }

              console.log(`[${getLocalTimeString()}] Incoming call type: ${callType}`);
            }
          }
        } catch (error) {
          console.error('Error determining incoming call type:', error);
        }

        setIsAgentReceivingCall(true);
        setIncomingCallType(callType);
        updateActiveCallState({
          call: incomingCall,
          status: CallStatus.Ringing,
          direction: CallDirection.INBOUND,
        });
      });

      device.on('offline', async () => {
        console.log(`[${getLocalTimeString()}] Device is offline, initiating reconnection...`);

        // Check if device is destroyed before attempting to reconnect
        if (isDeviceDestroyed.current) {
          console.log(`[${getLocalTimeString()}] Device is destroyed, skipping reconnection`);
          return;
        }

        notify('Device disconnected, reconnecting...', { type: 'warning' });

        // Use the centralized reconnection handler with exponential backoff
        await attemptReconnection(device);
      });

      device.on('unregistered', async () => {
        console.log(
          `[${getLocalTimeString()}] Device unregistered (state: ${
            device.state
          }), initiating reconnection...`,
        );

        if (isDeviceDestroyed.current) {
          console.log(`[${getLocalTimeString()}] Device is destroyed, skipping re-registration`);
          return;
        }

        // Use the centralized reconnection handler with exponential backoff
        await attemptReconnection(device);
      });

      device.on('tokenWillExpire', async () => {
        console.log(`[${getLocalTimeString()}] Device token will expire, refreshing...`);

        if (isDeviceDestroyed.current) {
          console.log(`[${getLocalTimeString()}] Device is destroyed, skipping token refresh`);
          return;
        }

        // Prevent duplicate token refresh during reconnection
        if (isReconnecting.current) {
          console.log(`[${getLocalTimeString()}] Reconnection in progress, skipping token refresh`);
          return;
        }

        // Use safe token update method with state guards
        const tokenUpdated = await safelyUpdateDeviceToken(device);

        if (!tokenUpdated) {
          console.error(
            `[${getLocalTimeString()}] Token refresh failed, initiating reconnection...`,
          );
          await attemptReconnection(device);
          return;
        }

        console.log(`[${getLocalTimeString()}] Device token refreshed successfully`);

        // If device became unregistered after token update, register it
        if (device.state === Device.State.Unregistered && !isDeviceDestroyed.current) {
          await safelyRegisterDevice(device);
        }

        // Reset reconnection attempts on successful token refresh
        reconnectAttempts.current = 0;
      });
    },
    [
      getAgentAvailability,
      notify,
      updateActiveCallState,
      setupIncomingCallEventListeners,
      attemptReconnection,
      safelyUpdateDeviceToken,
      safelyRegisterDevice,
    ],
  );

  // Call Management Functions
  const startOutgoingCall = useCallback(
    async (memberPhoneNumber: string): Promise<Call | null> => {
      if (!device) {
        notify('Device not ready', { type: 'error' });
        return null;
      }

      try {
        const call = await device.connect({
          params: {
            To: `+1${memberPhoneNumber}`,
          },
        });

        updateActiveCallState({
          call,
          muted: false,
          onHold: false,
          status: CallStatus.Calling,
          direction: CallDirection.OUTBOUND,
        });

        // Setup event listeners for outgoing call
        setupOutgoingCallEventListeners(call);

        return call;
      } catch (error) {
        console.error('Error starting outgoing call:', error);
        notify('Failed to start outgoing call', { type: 'error' });
        return null;
      }
    },
    [device, notify, updateActiveCallState, setupOutgoingCallEventListeners],
  );

  const setAudioRatingValue = useCallback((rating: number | null) => {
    setAudioRating(rating);
  }, []);

  const setCallPurposeValue = useCallback((purpose: CallPurpose) => {
    setCallPurpose(purpose);
  }, []);

  const saveCallSummary = useCallback(async () => {
    if (isSavingRating) {
      return;
    }

    try {
      // Only clear data and return to IDLE if call has ended AND user has completed rating
      if (
        !activeCallState.call &&
        (activeCallState.status === CallStatus.Ended ||
          activeCallState.status === CallStatus.Ending)
      ) {
        setIsSavingRating(true);

        // Always save notes if we have them and there's a call log
        if (callNotes && callLog) {
          await voiceService.addNote(callLog.id, { content: callNotes });
        }

        if (audioRating && callLog) {
          await voiceService.addNote(callLog.id, {
            content: `Audio quality rated ${audioRating}/5 stars`,
          });

          await voiceService.rateCall(callLog.id, {
            rating: audioRating,
          });
        }

        notify('Call summary saved successfully', { type: 'success' });

        clearCallData(); // Change to IDLE status after clearing data
        setIsSavingRating(false);
      }
    } catch (error) {
      console.error('Error saving call summary:', error);
      notify('Error saving call summary', { type: 'error' });
      setIsSavingRating(false);
    }
  }, [
    isSavingRating,
    callNotes,
    callLog,
    activeCallState.call,
    activeCallState.status,
    audioRating,
    clearCallData,
    notify,
  ]);

  const answerIncomingCall = useCallback(
    (call: Call) => {
      try {
        call.accept();
        setIsAgentReceivingCall(false);
        setIsIncomingCallModalOpen(true);
        setCurrentCallSid(call.parameters.CallSid);

        updateActiveCallState({
          call,
          muted: false,
          onHold: false,
          status: CallStatus.OnCall,
          direction: CallDirection.INBOUND,
          startTime: new Date(),
        });
      } catch (error) {
        console.error('Error answering incoming call:', error);
        notify('Failed to answer incoming call', { type: 'error' });
      }
    },
    [notify, updateActiveCallState],
  );

  const rejectIncomingCall = useCallback(
    (call: Call) => {
      try {
        call.reject();
        setIsAgentReceivingCall(false);

        updateActiveCallState({
          call: null,
          status: CallStatus.Rejected,
          direction: null,
          onHold: false,
        });
        clearCallData();
      } catch (error) {
        console.error('Error rejecting incoming call:', error);
        notify('Failed to reject incoming call', { type: 'error' });
      }
    },
    [updateActiveCallState, clearCallData, notify],
  );

  const hangUpCall = useCallback(async () => {
    const targetCall = activeCallState.call;

    if (!targetCall) {
      console.error(`No active call to hang up on device`);
      return;
    }

    try {
      // If there's an active transfer, cancel it first
      if (
        transferData &&
        (transferData.status === 'initiated' || transferData.status === 'ringing')
      ) {
        console.log(`Canceling transfer before hanging up`);
        await cancelTransfer();
      }

      targetCall.disconnect();
      console.log(`Call disconnected on device`);

      updateActiveCallState({
        call: null,
        status: CallStatus.Ending,
        endTime: new Date(),
        onHold: false,
        direction: null,
      });

      setIsAwaitingRating(true);
    } catch (error) {
      console.error(`Error hanging up call on device:`, error);
      notify(`Failed to hang up call on device`, { type: 'error' });
    }
  }, [activeCallState.call, transferData, cancelTransfer, notify, updateActiveCallState]);

  // Modal Management Functions
  const openOutgoingCallModal = useCallback(() => {
    setIsOutgoingCallModalOpen(true);
  }, []);

  const closeOutgoingCallModal = useCallback(() => {
    setIsOutgoingCallModalOpen(false);
  }, []);

  const openIncomingCallModal = useCallback(() => {
    setIsIncomingCallModalOpen(true);
  }, []);

  const closeIncomingCallModal = useCallback(() => {
    setIsIncomingCallModalOpen(false);
  }, []);

  // Navigation Functions
  const toggleReturnToCall = useCallback(() => {
    setReturnToCall((prev) => !prev);
  }, []);

  const updateCurrentCallSid = useCallback((callId: string | null) => {
    setCurrentCallSid(callId);
    setAgent((prev) => (prev ? { ...prev, currentCallId: callId || undefined } : null));
  }, []);

  // Device Initialization
  const initializeDevice = useCallback(async () => {
    if (device) {
      setIsDeviceOn(true);
      return;
    }

    try {
      const tokenResponse = await voiceService.generateAccessToken();

      if (!tokenResponse.success || !tokenResponse.data?.token) {
        // Thrown rather than returned so `toggleDevice`'s catch actually runs.
        // Returning here meant the device lock stayed held and the caller went on
        // to mark the agent AVAILABLE with no registered device.
        throw new Error('Failed to get access token for device');
      }

      const device = new Device(tokenResponse.data.token, {
        tokenRefreshMs: 30000, // Refresh token 30 seconds before expiration
        maxCallSignalingTimeoutMs: 30000, // Allow reconnection attempts to the same edge (server) before ending a call
        enableImprovedSignalingErrorPrecision: true, // It exposes more detailed Twilio error codes from errors
      });

      // Setup device event listeners
      await setupDeviceEventListeners(device);

      // Register the device
      device.register();

      setDevice(device);
      setIsDeviceOn(true);
    } catch (error) {
      console.error('Error initializing device:', error);
      notify('Failed to initialize device', { type: 'error' });
      // Rethrown so `toggleDevice` releases the device lock and skips marking the
      // agent AVAILABLE. Swallowing it here left the lock held by a tab with no
      // working device, and the only recovery was a page reload.
      throw error;
    }
  }, [device, notify, setupDeviceEventListeners]);

  // Device Toggle Function
  const toggleDevice = useCallback(async () => {
    if (!isDeviceOn || !device) {
      // Prevent enabling the device if it's already active in another tab
      if (!acquireLock()) {
        notify('Device is already active in another tab. Turn it off there first.', {
          type: 'warning',
        });
        return;
      }

      try {
        if (!audioContextRef.current) {
          audioContextRef.current = new AudioContext();
        }

        if (audioContextRef.current.state === 'suspended') {
          await audioContextRef.current.resume();
        }

        isDeviceDestroyed.current = false;
        notify('Device turning on...', { type: 'info' });

        await initializeDevice();
        await updateAgentAvailability({
          status: AgentAvailabilityStatus.AVAILABLE,
        });
      } catch (error) {
        console.error('Error turning on device:', error);
        notify('Failed to turn on device', { type: 'error' });
        releaseLock();
        setIsDeviceOn(false);
        isDeviceDestroyed.current = true;
      }
    } else {
      try {
        isDeviceDestroyed.current = true;
        releaseLock();

        // Update backend status BEFORE destroying device
        await updateAgentAvailability({
          status: AgentAvailabilityStatus.OFFLINE,
        });

        try {
          device.removeAllListeners();
          device.destroy();
          setDevice(null);
          setIsDeviceOn(false);
        } catch (deviceError) {
          console.error('Error during device cleanup:', deviceError);
          setDevice(null);
          setIsDeviceOn(false);
        }

        notify('Device turned off', { type: 'info' });
      } catch (error) {
        console.error('Error turning off device:', error);
        // Even if API call fails, still destroy device to clean up
        try {
          device.removeAllListeners();
          device.destroy();
          setDevice(null);
          setIsDeviceOn(false);
        } catch (deviceError) {
          console.error('Error during device cleanup in recovery:', deviceError);
          setDevice(null);
          setIsDeviceOn(false);
        }

        notify('Device turned off, but status update may have failed', { type: 'warning' });
      }
    }
  }, [
    isDeviceOn,
    device,
    updateAgentAvailability,
    notify,
    initializeDevice,
    acquireLock,
    releaseLock,
  ]);

  /**
   * Initialize agent data on mount.
   *
   * This effect failing is what produced the worst symptom in the whole flow:
   * `agent` stayed null, `CustomAppBar` gates the entire phone widget on
   * `agent?.trackingNumbers`, and so the power button simply never rendered —
   * no error, no explanation, and no way back short of a page reload. The
   * failure was `console.error`-only and the effect never re-ran.
   *
   * Three changes: the failure is now visible via `agentBootstrapState`,
   * `retryAgentBootstrap` re-runs it without a reload, and "your agent record is
   * missing" is distinguished from "the request failed" — previously both left
   * an identically blank app bar.
   */
  useEffect(() => {
    let isComponentMounted = true;

    const setOfflineAvailability = () => {
      agentAvailabilityRef.current = {
        previousStatus: AgentAvailabilityStatus.OFFLINE,
        currentStatus: AgentAvailabilityStatus.OFFLINE,
      };
      forceUpdateAgentAvailability({});
    };

    (async () => {
      try {
        const authToken = AuthService.getAuth()?.auth_token;
        if (!authToken || !isComponentMounted) {
          return;
        }

        setAgentBootstrapState('loading');

        // Prefer the profile stored at login. Avoids a network round-trip on
        // every mount, and avoids one more request to Core's
        // /v1/profile/:auth_token, which carries the token in the URL path.
        // `getLocalProfile` flattens the payload and returns `{}` when absent, so
        // fall back to the network only when there is no cached id.
        const cachedProviderId = AuthService.getLocalProfile()?.id;
        const providerId = cachedProviderId
          ? Number(cachedProviderId)
          : (await AuthService.getProfile(authToken)).user.id;

        if (!isComponentMounted) {
          return;
        }

        if (!providerId || Number.isNaN(providerId)) {
          throw new Error('Could not determine the current provider id');
        }

        const directoryResponse = await voiceService.getAgentDirectory();
        if (!isComponentMounted) {
          return;
        }

        if (!directoryResponse.success || !directoryResponse.data) {
          // The request itself failed. Distinct from "you have no agent record".
          throw new Error('Failed to load agent directory');
        }

        const currentAgent = directoryResponse.data[providerId] ?? null;

        setAgent(currentAgent);
        agentRef.current = currentAgent;
        setAgentBootstrapState(currentAgent ? 'ready' : 'not-configured');

        setOfflineAvailability();
        await updateAgentAvailability({ status: AgentAvailabilityStatus.OFFLINE });
        forceUpdateAgentAvailability({});
      } catch (error) {
        console.error('Failed to fetch agent directory:', error);
        reportMonitoredError({ error });

        if (isComponentMounted) {
          setAgentBootstrapState('error');
          setOfflineAvailability();
        }
      }
    })();

    return () => {
      isComponentMounted = false;
    };
  }, [updateAgentAvailability, agentBootstrapAttempt]);

  /**
   * Hand the session monitor a way to take the phone offline.
   *
   * Registered rather than imported the other way around so the monitor stays
   * independent of the comms context. The guard awaits these before signing out,
   * which is what stops a registered Twilio device surviving onto the login page.
   */
  useEffect(
    () => sessionMonitor.registerTeardown(() => markDeviceOffline('session-expired')),
    [markDeviceOffline],
  );

  // Opens the appropriate call modal when the user attempts to return to an active call
  useEffect(() => {
    if (returnToCall) {
      if (activeCallState.direction === CallDirection.INBOUND) {
        setIsIncomingCallModalOpen(true);
      }

      if (activeCallState.direction === CallDirection.OUTBOUND) {
        setIsOutgoingCallModalOpen(true);
      }
    }
  }, [returnToCall, activeCallState.direction]);

  // Cleanup on unmount
  useEffect(() => {
    const tokenInterval = tokenRefreshInterval.current;
    const callTimer = callDurationTimer.current;
    const reconnectTimer = reconnectTimeout.current;
    const currentDevice = device;

    return () => {
      if (tokenInterval) {
        clearInterval(tokenInterval);
      }

      if (callTimer) {
        clearInterval(callTimer);
      }

      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimeout.current = null;
      }

      if (currentDevice) {
        isDeviceDestroyed.current = true;
        isReconnecting.current = false;
        reconnectAttempts.current = 0;
        currentDevice.removeAllListeners();
        currentDevice.destroy();
      }
    };
  }, [device]);

  // Pusher WebSocket: Agent Private Channel
  // Always subscribed when agent is logged in, even when not on a call
  useEffect(() => {
    if (!pusher.isConnected || !agent?.coreProviderId) {
      return;
    }

    console.log('[PhoneCallsContext] Subscribing to agent channel:', agent.coreProviderId);

    const agentChannel = pusher.subscribe(`private-agent-${agent.coreProviderId}`);

    if (!agentChannel) {
      console.warn('[PhoneCallsContext] Failed to subscribe to agent channel');
      return;
    }

    // Transfer Event Handlers
    const handleTransferInitiated = (data: TransferEventPayload) => {
      console.log('[PhoneCallsContext] Transfer initiated:', data);
      // Transfer already initiated via initiateWarmTransfer/initiateBlindTransfer
      // This event confirms the backend processed it
    };

    const handleTransferRinging = (data: TransferEventPayload) => {
      console.log('[PhoneCallsContext] Transfer ringing:', data);
      setTransferData((prev) =>
        prev && prev.transferCallLogId === String(data.transferCallLogId)
          ? { ...prev, status: 'ringing' }
          : prev,
      );
      notify(`${data.targetAgent.name}'s phone is ringing...`, { type: 'info' });
    };

    const handleTransferAnswered = (data: TransferEventPayload) => {
      console.log('[PhoneCallsContext] Transfer answered:', data);
      notify(`${data.targetAgent.name} answered! They are now on the call.`, { type: 'success' });

      // Update agent directory to show target agent as ON_CALL
      setAgentDirectory((prev) =>
        prev.map((a) =>
          a.coreProviderId === data.targetAgent.coreProviderId
            ? { ...a, status: AgentAvailabilityStatus.ON_CALL }
            : a,
        ),
      );

      if (data.transferType === TransferType.BLIND) {
        setTransferData(null);
        console.log(
          '[PhoneCallsContext] Blind transfer completed - agent will be disconnected by backend',
        );
      } else {
        setTransferData(null);
      }
    };

    const handleTransferCompleted = (data: TransferEventPayload) => {
      console.log('[PhoneCallsContext] Transfer completed:', data);
      setTransferData(null);
      notify(`Transfer to ${data.targetAgent.name} completed successfully!`, { type: 'success' });

      // Refresh agent directory to update statuses
      refreshAgentDirectory();
    };

    const handleTransferFailed = (data: TransferEventPayload) => {
      console.log('[PhoneCallsContext] Transfer failed:', data);

      const reason = data.reason || 'Agent did not answer';
      notify(`Transfer to ${data.targetAgent.name} failed: ${reason}`, { type: 'error' });

      // Close transfer status modal and directory
      setTransferData(null);

      // Unhold the caller if they were put on hold
      const callerPhone = memberPhoneNumberRef.current || selectedMemberRef.current?.phone;
      if (callerPhone) {
        holdUnholdConference(false, callerPhone).catch((err) => {
          console.error('[PhoneCallsContext] Failed to unhold caller after transfer failure:', err);
        });
      }
    };

    const handleTransferCanceled = (data: TransferEventPayload) => {
      console.log('[PhoneCallsContext] Transfer canceled:', data);
      setTransferData(null);
      notify(`Transfer to ${data.targetAgent.name} was canceled`, { type: 'info' });
    };

    const handleTransferNoAnswer = (data: TransferEventPayload) => {
      console.log('[PhoneCallsContext] Transfer no answer:', data);
      notify(`${data.targetAgent.name} did not answer`, { type: 'warning' });

      // Close transfer status modal and directory
      setTransferData(null);
    };

    const handleTransferBusy = (data: TransferEventPayload) => {
      console.log('[PhoneCallsContext] Transfer busy:', data);
      notify(`${data.targetAgent.name} is busy`, { type: 'warning' });

      // Close transfer status modal and directory
      setTransferData(null);
    };

    const handleVoicemailReceived = (data: VoicemailEventPayload) => {
      console.log('[PhoneCallsContext] Voicemail received:', data);

      const fromName = data.fromName || data.fromNumber || 'Unknown';
      const fromNumber = data.fromNumber || '';
      const duration = String(data.duration ?? 0);

      setVoicemailNotifications((prev) => [
        ...prev,
        {
          id: `voicemail-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          fromName,
          fromNumber,
          duration,
        },
      ]);

      try {
        const audio = new Audio('/sounds/new-notification-1.mp3');
        audio.volume = 0.5;
        audio.play().catch((err) => {
          console.warn('[PhoneCallsContext] Voicemail failed to play notification sound:', err);
        });
      } catch (e) {
        console.warn('[PhoneCallsContext] Voicemail audio notification error:', e);
      }
    };

    const handleSmsReceived = (data: SmsEventPayload) => {
      console.log('[PhoneCallsContext] SMS received:', data);

      setSmsNotifications((prev) => [
        ...prev,
        {
          id: `sms-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          fromNumber: data.fromNumber,
        },
      ]);

      try {
        const audio = new Audio('/sounds/new-notification-1.mp3');
        audio.volume = 0.5;
        audio.play().catch((err) => {
          console.warn('[PhoneCallsContext] SMS Failed to play notification sound:', err);
        });
      } catch (e) {
        console.warn('[PhoneCallsContext] SMS Audio notification error:', e);
      }
    };

    // Bind transfer events to agent channel
    agentChannel.bind(CallTransferEventName.TransferInitiated, handleTransferInitiated);
    agentChannel.bind(CallTransferEventName.TransferRinging, handleTransferRinging);
    agentChannel.bind(CallTransferEventName.TransferAnswered, handleTransferAnswered);
    agentChannel.bind(CallTransferEventName.TransferCompleted, handleTransferCompleted);
    agentChannel.bind(CallTransferEventName.TransferFailed, handleTransferFailed);
    agentChannel.bind(CallTransferEventName.TransferCanceled, handleTransferCanceled);
    agentChannel.bind(CallTransferEventName.TransferNoAnswer, handleTransferNoAnswer);
    agentChannel.bind(CallTransferEventName.TransferBusy, handleTransferBusy);
    agentChannel.bind(CommunicationCenterEventName.IncomingVoicemail, handleVoicemailReceived);
    agentChannel.bind(CommunicationCenterEventName.IncomingSms, handleSmsReceived);

    // Cleanup: Unbind all events when agent logs out or component unmounts
    return () => {
      console.log('[PhoneCallsContext] Unsubscribing from agent channel');
      agentChannel.unbind_all();
      pusher.unsubscribe(`private-agent-${agent.coreProviderId}`);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pusher.isConnected,
    agent?.coreProviderId,
    notify,
    refreshAgentDirectory,
    holdUnholdConference,
  ]);

  // Pusher WebSocket: Global Agents Directory Channel for real-time status updates
  // This allows the Directory component to reflect agent status changes in real-time
  useEffect(() => {
    if (!pusher.isConnected || !agent?.coreProviderId) {
      return;
    }

    console.log('[PhoneCallsContext] Subscribing to global agents directory channel');

    const agentsDirectoryChannel = pusher.subscribe('private-agents-directory');

    if (!agentsDirectoryChannel) {
      console.warn('[PhoneCallsContext] Failed to subscribe to agents directory channel');
      return;
    }

    // Force a fresh fetch whenever Pusher (re)connects to avoid stale agent statuses
    // after brief disconnects or missed AgentStatusChanged events.
    refreshAgentDirectory();

    // Handle agent status change events
    const handleAgentStatusChanged = (data: AgentStatusEventPayload) => {
      console.log('[PhoneCallsContext] Agent status changed:', data);

      // Update the agent directory with the new status
      setAgentDirectory((prev) =>
        prev.map((a) =>
          a.coreProviderId === data.coreProviderId
            ? { ...a, status: data.newStatus as AgentAvailabilityStatus }
            : a,
        ),
      );
      setAgentDirectoryLastUpdatedAt(new Date());
    };

    // Bind the agent status change event
    agentsDirectoryChannel.bind(
      CommunicationCenterEventName.AgentStatusChanged,
      handleAgentStatusChanged,
    );

    // Cleanup: Unbind all events when agent logs out or component unmounts
    return () => {
      console.log('[PhoneCallsContext] Unsubscribing from agents directory channel');
      agentsDirectoryChannel.unbind_all();
      pusher.unsubscribe('private-agents-directory');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pusher.isConnected, agent?.coreProviderId]);

  // Polling fallback for the agent directory. Pusher is the primary sync channel, but
  // brief disconnects or missed AgentStatusChanged events can leave the directory stale,
  // which makes agents look offline/unavailable during call transfers. Poll every 45s
  // while the agent is logged in and the device is active.
  useEffect(() => {
    if (!agent?.coreProviderId || !isDeviceOn) {
      return;
    }

    const AGENT_DIRECTORY_POLL_INTERVAL_MS = 45000;
    const intervalId = window.setInterval(() => {
      // Stop polling once the breaker is open or the session is gone; otherwise
      // this keeps hitting an endpoint that cannot succeed.
      if (directoryBreakerOpen.current || sessionMonitor.isSessionDead()) {
        return;
      }
      refreshAgentDirectory();
    }, AGENT_DIRECTORY_POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [agent?.coreProviderId, isDeviceOn, refreshAgentDirectory]);

  // Pusher WebSocket: Conference Presence Channel for Participant Events
  // Only subscribed when agent has an active call in a conference
  useEffect(() => {
    // Only subscribe if pusher is connected, agent exists, AND there's an active conference
    if (!pusher.isConnected || !agent?.coreProviderId || !conference?.id) {
      return;
    }

    // Additional check: Only subscribe if agent has an active call
    const hasActiveCall = activeCallState.call;

    if (!hasActiveCall) {
      console.log(
        '[PhoneCallsContext] Skipping conference subscription - no active call (status:',
        activeCallState.status,
        ')',
      );
      return;
    }

    console.log(
      '[PhoneCallsContext] Subscribing to conference channel:',
      `presence-conference-${conference.id}`,
    );

    const conferenceChannel = pusher.subscribe(`presence-conference-${conference.id}`);

    if (!conferenceChannel) {
      console.warn('[PhoneCallsContext] Failed to subscribe to conference channel');
      return;
    }

    // Participant Event Handlers (for hold/unhold, join/leave)
    const handleParticipantHeld = (data: ParticipantEventPayload) => {
      console.log('[PhoneCallsContext] Participant held:', data);

      // Update active call state if this participant is the current agent
      if (data.coreProviderId === agent.coreProviderId) {
        updateActiveCallState({ onHold: true });
      }
    };

    const handleParticipantUnheld = (data: ParticipantEventPayload) => {
      console.log('[PhoneCallsContext] Participant unheld:', data);

      // Update active call state if this participant is the current agent
      if (data.coreProviderId === agent.coreProviderId) {
        updateActiveCallState({ onHold: false });
      }
    };

    const handleParticipantJoined = (data: ParticipantEventPayload) => {
      console.log('[PhoneCallsContext] Participant joined:', data);

      // Update agent directory if an agent joined
      if (data.participantType === ParticipantType.Agent && data.coreProviderId) {
        setAgentDirectory((prev) =>
          prev.map((a) =>
            a.coreProviderId === data.coreProviderId
              ? { ...a, status: AgentAvailabilityStatus.ON_CALL }
              : a,
          ),
        );
      }
    };

    const handleParticipantLeft = (data: ParticipantEventPayload) => {
      console.log('[PhoneCallsContext] Participant left:', data);

      // Update agent directory if an agent left
      if (data.participantType === ParticipantType.Agent && data.coreProviderId) {
        setAgentDirectory((prev) =>
          prev.map((a) =>
            a.coreProviderId === data.coreProviderId
              ? { ...a, status: AgentAvailabilityStatus.AVAILABLE }
              : a,
          ),
        );
      }
    };

    const handleParticipantMuted = (data: ParticipantEventPayload) => {
      console.log('[PhoneCallsContext] Participant muted:', data);
      // Could update UI to show muted state for other participants
    };

    const handleParticipantUnmuted = (data: ParticipantEventPayload) => {
      console.log('[PhoneCallsContext] Participant unmuted:', data);
      // Could update UI to show unmuted state for other participants
    };

    const handleConferenceEnded = (data: ParticipantEventPayload) => {
      console.log('[PhoneCallsContext] Conference ended:', data);
      // Conference ended - cleanup will happen via dependency change
    };

    // Bind participant events to conference channel
    conferenceChannel.bind(ParticipantEventName.ParticipantHeld, handleParticipantHeld);
    conferenceChannel.bind(ParticipantEventName.ParticipantUnheld, handleParticipantUnheld);
    conferenceChannel.bind(ParticipantEventName.ParticipantJoined, handleParticipantJoined);
    conferenceChannel.bind(ParticipantEventName.ParticipantLeft, handleParticipantLeft);
    conferenceChannel.bind(ParticipantEventName.ParticipantMuted, handleParticipantMuted);
    conferenceChannel.bind(ParticipantEventName.ParticipantUnmuted, handleParticipantUnmuted);
    conferenceChannel.bind(ParticipantEventName.ConferenceEnded, handleConferenceEnded);

    // Cleanup: Unbind and unsubscribe when call ends, conference changes, or component unmounts
    return () => {
      console.log('[PhoneCallsContext] Unsubscribing from conference channel:', conference.id);
      conferenceChannel.unbind_all();
      pusher.unsubscribe(`presence-conference-${conference.id}`);
    };
    // Only re-subscribe when connection status, agent, conference, or call status changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pusher.isConnected,
    agent?.coreProviderId,
    conference?.id,
    activeCallState.status,
    activeCallState.call,
    updateActiveCallState,
  ]);

  // Auto-sync phone number when selected member changes (only for outgoing calls when not in active call)
  useEffect(() => {
    if (
      selectedMember &&
      !activeCallState.call &&
      !isAwaitingRating &&
      activeCallState.status === CallStatus.Idle
    ) {
      setMemberPhoneNumber(selectedMember.phone.replaceAll('+1', '') || '');
    }
  }, [selectedMember, activeCallState.call, isAwaitingRating, activeCallState.status]);

  // Handle Call Purpose Changes
  const handleCallPurposeChange = useCallback(
    async (newPurpose: CallPurpose, callLogId: number) => {
      try {
        const response = await voiceService.updateCallPurpose(callLogId, newPurpose);
        if (!response.success) {
          notify('Failed to update call purpose', 'error');
        }
      } catch (error) {
        console.error('Error updating call purpose:', error);
        notify('Failed to update call purpose', 'error');
      }
    },
    [notify],
  );

  useEffect(() => {
    (async () => {
      if (activeCallState.call && callLog?.id) {
        await handleCallPurposeChange(callPurpose, callLog?.id);
      }
    })();
  }, [activeCallState.call, handleCallPurposeChange, callPurpose, callLog?.id]);

  // Handle tab close - turn device offline using keepalive fetch for reliability
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isDeviceOn && device) {
        releaseLock();
        voiceService.updateAgentAvailabilityWithKeepalive({
          status: AgentAvailabilityStatus.OFFLINE,
          coreProviderId: agentRef.current?.coreProviderId,
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDeviceOn, device, releaseLock]);

  const contextValue: PhoneCallsContextType = {
    // Agent Management
    agent,
    agentAvailability: agentAvailabilityRef.current,
    agentBootstrapState,
    retryAgentBootstrap,
    updateAgentAvailability,

    // Device Management
    device,
    isDeviceOn,
    isDeviceLockedInOtherTab: isLockedByOtherTab,
    toggleDevice,
    activeCallState,

    // Active Call Management
    currentCallSid,
    conference,

    // Call State Computed Properties
    callDuration,
    isMuted,
    isOnHold,
    callLog,

    // Incoming Call
    isAgentReceivingCall,
    incomingCallType,

    // Modal States
    isOutgoingCallModalOpen,
    isIncomingCallModalOpen,
    isDirectoryOpen,
    setIsDirectoryOpen,

    // Member Management
    selectedMember,
    setSelectedMember,

    // Phone Number Management
    memberPhoneNumber,
    setMemberPhoneNumber,

    // Transfer Management
    transferData,
    agentDirectory,
    agentDirectoryLastUpdatedAt,
    canInitiateTransfer,
    refreshAgentDirectory,

    // Call Actions
    startOutgoingCall,
    answerIncomingCall,
    rejectIncomingCall,
    hangUpCall,
    muteCall,
    holdUnholdConference,
    sendDigitsOnCall,

    // Transfer Actions
    initiateWarmTransfer,
    initiateBlindTransfer,
    cancelTransfer,

    // Device Management
    updateActiveCallState,

    // Modal Management
    openOutgoingCallModal,
    closeOutgoingCallModal,
    openIncomingCallModal,
    closeIncomingCallModal,

    // Navigation
    returnToCall,
    toggleReturnToCall,

    // Utility
    updateCurrentCallSid,
    clearCallData,

    // Rating State Management
    isAwaitingRating,
    audioRating,
    isSavingRating,
    setAwaitingRating: setIsAwaitingRating,
    setAudioRating: setAudioRatingValue,

    // Call Purpose Management
    callPurpose,
    setCallPurpose: setCallPurposeValue,

    // Notes Management
    callNotes,
    setCallNotes,
    saveCallSummary,
  };

  const formatDuration = (seconds: string) => {
    const totalSeconds = parseInt(seconds, 10) || 0;
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    if (mins > 0) {
      return `${mins}m ${secs}s`;
    }
    return `${secs}s`;
  };

  const handleCloseVoicemailNotification = (id: string) => {
    setVoicemailNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const handleCloseSmsNotification = (id: string) => {
    setSmsNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  return (
    <PhoneCallsContext.Provider value={contextValue}>
      {children}

      <NotificationStack>
        {voicemailNotifications.map((notification) => (
          <Notification
            key={notification.id}
            open={true}
            onClose={() => handleCloseVoicemailNotification(notification.id)}
            icon={<VoicemailIcon sx={{ color: '#AF77BE', fontSize: 24 }} />}
            accentColor='#AF77BE'
          >
            <Typography
              variant='subtitle1'
              sx={{ fontWeight: 600, color: '#1a1a1a', mb: 0.5, fontSize: '15px' }}
            >
              New Voicemail
            </Typography>
            <Typography variant='body2' sx={{ color: '#666', mb: 0.5 }}>
              From: <strong>{notification.fromName || 'Unknown'}</strong>
            </Typography>
            {notification.fromNumber && (
              <Typography variant='body2' sx={{ color: '#888', fontSize: '13px' }}>
                {notification.fromNumber}
              </Typography>
            )}
            {notification.duration && (
              <Typography
                variant='caption'
                sx={{
                  color: '#AF77BE',
                  fontWeight: 500,
                  mt: 0.5,
                  display: 'block',
                }}
              >
                Duration: {formatDuration(notification.duration)}
              </Typography>
            )}
          </Notification>
        ))}

        {smsNotifications.map((notification) => (
          <Notification
            key={notification.id}
            open={true}
            onClose={() => handleCloseSmsNotification(notification.id)}
            icon={<SmsIcon sx={{ color: '#4CAF50', fontSize: 24 }} />}
            accentColor='#4CAF50'
          >
            <Typography
              variant='subtitle1'
              sx={{ fontWeight: 600, color: '#1a1a1a', mb: 0.5, fontSize: '15px' }}
            >
              New SMS Message
            </Typography>
            <Typography variant='body2' sx={{ color: '#666', mb: 0.5 }}>
              From: <strong>{notification.fromNumber || 'Unknown'}</strong>
            </Typography>
          </Notification>
        ))}
      </NotificationStack>
    </PhoneCallsContext.Provider>
  );
};

export const usePhoneCalls = () => {
  const context = useContext(PhoneCallsContext);
  if (context === undefined) {
    throw new Error('usePhoneCalls must be used within a PhoneCallsProvider');
  }
  return context;
};
